import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface Comment {
  commentId: string;
  ticketId: string;
  content: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;
  updatedBy?: string;
  updatedByName?: string;
}

function iso(d: unknown): string {
  if (typeof d === 'string' && d) return d;
  if (d instanceof Date) return d.toISOString();
  return new Date().toISOString();
}

/** Maps .NET `TicketComment` JSON (camelCase `id`) to the shape the UI expects. */
export function normalizeCommentFromApi(raw: unknown): Comment {
  const c = raw as Record<string, unknown>;
  const id = c.id != null ? String(c.id) : c.commentId != null ? String(c.commentId) : '';
  return {
    commentId: id,
    ticketId: String(c.ticketId ?? ''),
    content: String(c.content ?? ''),
    isInternal: Boolean(c.isInternal),
    createdAt: iso(c.createdAt),
    updatedAt: iso(c.updatedAt),
    createdBy: String(c.createdBy ?? ''),
    createdByName: String(c.createdByName ?? ''),
    updatedBy: c.updatedBy != null ? String(c.updatedBy) : undefined,
    updatedByName: c.updatedByName != null ? String(c.updatedByName) : undefined,
  };
}

function parseCommentsResponse(body: unknown): Comment[] {
  if (Array.isArray(body)) {
    return body.map(normalizeCommentFromApi);
  }
  if (body && typeof body === 'object' && 'data' in body) {
    const inner = (body as { data?: unknown }).data;
    if (Array.isArray(inner)) return inner.map(normalizeCommentFromApi);
    const comments = (inner as { comments?: unknown } | undefined)?.comments;
    if (Array.isArray(comments)) return comments.map(normalizeCommentFromApi);
  }
  return [];
}

async function getComments(ticketId: string, token: string | undefined, workspaceId: string): Promise<Comment[]> {
  const bearer = resolveBearerToken(token);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.get<unknown>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/${ticketId}/comments`,
    { headers },
  );

  return parseCommentsResponse(response.data);
}

function extractWorkspaceId(workspaceObj: unknown): string {
  const w = workspaceObj as { id?: string; SK?: string } | null | undefined;
  if (!w) return '';
  if (w.id) return w.id;
  if (w.SK) {
    if (w.SK.startsWith('WS#')) return w.SK.substring(3);
    return w.SK;
  }
  return '';
}

export function useGetComments(ticketId: string | null) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const hasBearer = !!resolveBearerToken(sessionJwt);

  return useQuery<Comment[]>({
    queryKey: [queryKeys.comments, ticketId, workspaceId],
    queryFn: () => {
      if (!ticketId) throw new Error('No ticket ID provided');
      return getComments(ticketId, sessionJwt, workspaceId);
    },
    enabled: !!ticketId && !!workspaceId && hasBearer,
    staleTime: 0,
    cacheTime: 5 * 60 * 1000,
  });
}
